import {compress, decompress} from 'lz-string';
import ServiceResponse from '../models/ServiceResponse';

export default class LocalStorageService 
{

    // Initializing important variables
    /*
    constructor() {
        this.configService = new ConfigService();
        this.useCompression = this.configService.getValue("localcompression");
    }
    */

    prefix()
    {
        return "gcobs";
    }

    setKey(key, val){
        var storedKey = (this.useCompression) ? compress(key): key;
        var storedVal = (this.useCompression) ? compress(val): val;
        localStorage.setItem(storedKey, storedVal);
    }

    getKey(key){
        let storedKey = (this.useCompression) ? decompress(key): key;
        let val = localStorage.getItem(storedKey);
        let storedVal = (this.useCompression) ? decompress(val): val;
        return storedVal;
    }

    keyExists(key){
        let storedKey = (this.useCompression) ? decompress(key): key;
        return (this.getKey(storedKey)!=null);
    }

    removeKey(key){
        let storedKey = (this.useCompression) ? decompress(key): key;
        localStorage.removeItem(storedKey);    
    }



    saveSingleVal(key, val, shouldCompress=false)
    {
        let storedVal = (shouldCompress) ? compress(val) : val;
        localStorage.setItem(key, storedVal);
    }

    getSingleVal(key, shouldDecompress=false)
    {
        var item = localStorage.getItem(key);
        return (shouldDecompress) ? decompress(item) : item;
    }

    saveObjectVal(key, obj, shouldCompress=false)
    {
        let storedObj = (shouldCompress) ? compress(JSON.stringify(obj)) : JSON.stringify(obj);
        localStorage.setItem(key, storedObj);
    }

    getObjectVal(key, shouldDecompress=false)
    {
        let storedObj = localStorage.getItem(key);
        var result = (shouldDecompress) ? JSON.parse(decompress(storedObj)) : JSON.parse(storedObj);
        return result;
    }

    hasKey(key)
    {
        return (localStorage.getItem(key)!=null);
    }

  
    // #region Session

    toSessionKey()
    {
        return this.prefix() + "~lastusersession";
    }

    hasLastUserSession()
    {
        return (this.hasKey(this.toSessionKey));
    }

    getLastUserSession()
    {
        return (this.hasKey(this.toSessionKey())) ? this.getObjectVal(this.toSessionKey(), false) : {isValid: false};
    }

    saveLastUserSession(userSession)
    {
        this.saveObjectVal(this.toSessionKey(), userSession, false);
    }

    removeLastUserSession(userSession)
    {
        this.removeKey(this.toSessionKey());
    }

    // endregion Session

    toUserKey(gcid, uid, keyTypeId="")
    {
        return this.prefix() + "~" + gcid + "~" + uid +
            (keyTypeId !== "") ? "~" + keyTypeId.toLowerCase().trim(): "";
    }

    toLastUserInfoKey()
    {
        return this.prefix() + "~lastuserinfo";
    }


    hasLastUserInfo()
    {
        return (this.hasKey(this.toLastUserInfoKey()));
    }

    getLastUserInfo()
    {
        return (this.hasKey(this.toLastUserInfoKey())) ? this.getObjectVal(this.toLastUserInfoKey(), false) : {isValid: false};
    }

    saveLastUserInfo(userInfo)
    {
        this.saveObjectVal(this.toLastUserInfoKey(), {"gcid": userInfo.gcid, "uid": userInfo.uid, "lastlocationid": userInfo.currentLocationId, "locationinfo": userInfo.locationInfo}, false);
    }

    toAppInfoKey(gcid, uid)
    {
        return this.prefix() + "~" + gcid + "~" + uid + "~appinfo";
    }

    hasAppInfo(gcid, uid)
    {
        return this.hasKey(this.toAppInfoKey(gcid, uid));
    }

    saveAppInfo(gcid, uid, appInfo)
    {
        return this.saveObjectVal(this.toAppInfoKey(gcid, uid), appInfo, false);
    }

    getAppInfo(gcid, uid, appInfo)
    {
        return this.getObjectVal(this.toAppInfoKey(gcid, uid), false);
    }

    // #region OrgConfig

    toOrgConfigKey(gcid)
    {
        return this.prefix() + "~" + gcid + "~orgconfig";
    }

    saveOrgConfig(gcid, orgConfig){
        localStorage.setItem(this.toOrgConfigKey(gcid), compress(JSON.stringify(orgConfig)));
    }

    getOrgConfig(gcid){
        return JSON.parse(decompress(localStorage.getItem(this.toOrgConfigKey(gcid))));
    }

    hasOrgConfig(gcid){
        return this.keyExists(this.toOrgConfigKey(gcid));
    }

    // #endregion OrgConfig


    // #region Schemas

    toSchemaKey(schemaId)
    {
        return this.prefix() + "~" + schemaId + "~schema";
    }

    saveSchema(schemaId, schema)
    {
        localStorage.setItem(this.toSchemaKey(schemaId), compress(JSON.stringify(schema)));
    }

    getSchema(schemaId){
        var result =  JSON.parse(decompress(localStorage.getItem(this.toSchemaKey(schemaId))));
        return result;
    }

    toSchemaGridKey(schemaId)
    {
        return this.prefix() + "~" + schemaId + "~gridschema";
    }

    saveGridSchema(schemaId, gridSchema){
        localStorage.setItem(this.toSchemaGridKey(schemaId), compress(JSON.stringify(gridSchema))); 
    }

    getGridSchema(schemaId){
        //console.group("getLocalGridSchema: ");
        //console.log(schemaId);
        return {data: JSON.parse(decompress(localStorage.getItem(this.toSchemaGridKey(schemaId))))};  
        //console.groupEnd();
    }


    getSchemaReports(schemaId)
    {
        if (this.hasKey(this.toSchemaKey(schemaId)))
            var schema =  JSON.parse(decompress(localStorage.getItem(this.toSchemaKey(schemaId))));
        return schema?.reports?? [];
    }


    toDataListKey(gcid, listName)
    {
        return this.prefix() + "~" + gcid + "~" + listName + "~datalist";
    }

    saveDataList(dataList, gcid)
    {
        localStorage.setItem(this.toDataListKey(gcid, dataList.metaData.listName), compress(JSON.stringify(dataList))); 
    }

    getDataList(listName, gcid)
    {
        return {data: JSON.parse(decompress(localStorage.getItem(this.toDataListKey(gcid, listName))))}; 
    }

    toLookupEntityListKey(gcid, entityListType)
    {
        return this.prefix() + "~" + gcid + "~" + entityListType + "~lentitylist";
    }

    saveLookupEntityList(entityListType, lookupEntityList, gcid)
    {
        localStorage.setItem(this.toLookupEntityListKey(gcid, entityListType), compress(JSON.stringify(lookupEntityList))); 
    }

    getLookupEntityList(entityListType, gcid)
    {
        return {data: JSON.parse(decompress(localStorage.getItem(this.toLookupEntityListKey(gcid, entityListType))))}; 
    }

    getLookupEntity(entityListType, entityKey, gcid)
    {
        var listResult = this.getLookupEntityList(entityListType, gcid);
        
        var result = listResult.data.find((l) => l.metaData.entityType === entityListType && l.metaData.entityKey === entityKey);
        if (!result)
        {
            return { httpStatusCode: "404", hasError: true, messages: ["Unable to locate " + entityListType + " with key " + entityKey],
                isRecordNotFound: true, dataResult: null};
        }
        else
        {
            return { httpStatusCode: "200", hasError: false, messages: [], isRecordNotFound: false, dataResult: result};
        }
    }

    deleteLookupEntity(entityListType, entityKey, gcid)
    {
        try
        {
            var listResult = this.getLookupEntityList(entityListType, gcid);
            var idx = listResult.data.indexOf((l) => l.metaData.entityType === entityListType && l.metaData.entityKey === entityKey);
            if (idx >= 0)
            {
                var newListResult = listResult.slice(idx, 1);
                this.saveLookupEntityList(entityListType, newListResult, gcid);
                return new ServiceResponse(false, "Removed Key " + entityKey + " from Lookup Entity List " + entityListType);
            }
        }
        catch(e)
        {
            var msgs = [];
            msgs.push("Problem Removing Key " + entityKey + " from Lookup Entity List " + entityListType + ".");
            msgs.push(e);
            return new ServiceResponse(true, msgs);
        }
    }



    userPropKey(gcid, uid, propKey)
    {
        return gcid + "~" + uid + "~" + propKey;
    }


    setUserProp(gcid, uid, propKy, propVal)
    {
        localStorage.setItem(this.userPropKey(gcid, uid, propKy), propVal);
    }

    getUserProp(gcid, uid, propKy)
    {
        return localStorage.getItem(this.userPropKey(gcid, uid, propKy));
    }


    // #endregion Schemas


    //#region Suspended Observations


    toSuspendedObservationKey(gcid, schemaId, uid)
    {
        return this.prefix() + "~" + gcid + "~" + uid + "~" + schemaId + "~suspended";
    }

    saveLocalSuspendedObservation(gcid, schemaId, userId, valueBag)
    {
        localStorage.setItem(this.toSuspendedObservationKey(gcid,schemaId, userId), compress(JSON.stringify(valueBag)));
    }

    hasLocalSuspendedObservation(gcid, schemaId, userId)
    {
        return this.keyExists(this.toSuspendedObservationKey(gcid, schemaId, userId));
    }

    getLocalSuspendedObservation(gcid, schemaId, userId)
    {
        var result =  JSON.parse(decompress(localStorage.getItem(this.toSuspendedObservationKey(gcid, schemaId, userId))));
        return result;
    }

    removeLocalSuspendedObservation(gcid, schemaId, userId)
    {
        localStorage.removeItem(this.toSuspendedObservationKey(gcid, schemaId, userId));
    }

    removeAllLocalSuspendedObservations(gcid, userId)
    {
        var result = [];
        for (var key in localStorage){
            if (key.startsWith(this.prefix() + "~" + gcid + "~" + userId + "~") && key.endsWith("~suspended"))
            {   
                result.push(key);
            }
        }
        if ((!result) || (result.length < 1)) return;
        result.forEach((key) => 
        {
            localStorage.removeItem(key);    
        })
    }

    //#endregion

    //#region Address Book
    toAddressBookKey(gcid, uid)
    {
        return this.userPropKey(gcid, uid, "addressbook");
    }

    getLocalAddressBook(gcid, uid)
    {
        return {data: JSON.parse(decompress(localStorage.getItem(this.toAddressBookKey(gcid, uid))))};
    }

    saveLocalAddressBook(gcid, uid, addressBook)
    {
        localStorage.setItem(this.toAddressBookKey(gcid, uid), compress(JSON.stringify(addressBook))); 
    }

    hasLocalAddressBook(gcid, uid)
    {
        return this.keyExists(this.toAddressBookKey(gcid, uid));
    }

    // #endregion


}